import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, TextBox, CheckBox, Button, Icon, Card } from '../../../components/common';
import { useAuth } from '../../../contexts/AuthContext';
import { authService } from '../../../api/services/authService';
import TermsModal from '../../../components/service/TermsModal';
import { translateApiError } from '../../../utils/apiError';

export default function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    name: '',
  });
  const [errors, setErrors] = useState({});

  // Row 28: 회원가입 약관 — 개인정보 "처리방침"은 동의 항목 아님 (정책 안내용)
  // age14 → service(이용약관) → privacyConsent(수집·이용 동의) → refund → marketing(선택)
  const TERMS_LIST = [
    { key: 'age14', label: t('common.age_14_or_above'), required: true, slug: null },
    { key: 'service', label: t('footer.terms'), required: true, slug: 'service' },
    { key: 'privacyConsent', label: t('common.privacy_consent'), required: true, slug: 'privacyConsent' },
    { key: 'refund', label: t('footer.refund'), required: true, slug: 'refund' },
    { key: 'marketing', label: t('footer.marketing'), required: false, slug: 'marketing' },
  ];

  const [agreements, setAgreements] = useState(
    Object.fromEntries([['all', false], ...TERMS_LIST.map((t) => [t.key, false])])
  );
  const [termsModal, setTermsModal] = useState(null);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setErrors((p) => ({ ...p, [field]: '' }));
  };

  const handleAllAgree = () => {
    const next = !agreements.all;
    const updated = { all: next };
    TERMS_LIST.forEach((t) => { updated[t.key] = next; });
    setAgreements(updated);
  };

  const handleSingleAgree = (key) => () => {
    const next = { ...agreements, [key]: !agreements[key] };
    next.all = TERMS_LIST.every((t) => next[t.key]);
    setAgreements(next);
  };

  const allRequiredAgreed = TERMS_LIST.filter((t) => t.required).every((t) => agreements[t.key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.email.trim()) newErrors.email = t('auth.alert_email_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = t('auth.alert_email_invalid');
    if (!form.password.trim()) newErrors.password = t('auth.alert_password_required');
    else if (form.password.trim().length < 8) newErrors.password = t('auth.alert_password_min');
    else if (!/[A-Za-z]/.test(form.password)) newErrors.password = t('auth.alert_password_letter');
    else if (!/[0-9]/.test(form.password)) newErrors.password = t('auth.alert_password_number');
    if (form.password !== form.passwordConfirm) newErrors.passwordConfirm = t('auth.alert_password_mismatch');
    if (!form.name.trim()) newErrors.name = t('auth.alert_name_required');
    if (!allRequiredAgreed) newErrors.agreements = t('auth.alert_agreements_required');
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await authService.signup({
        email: form.email,
        password: form.password,
        name: form.name,
        terms_agreed: allRequiredAgreed,
        privacy_agreed: agreements.privacyConsent,
        marketing_agreed: agreements.marketing,
      });
      // 가입 성공 후 자동 로그인
      await login({ email: form.email, password: form.password });
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || '';
      // BE 가 X-Error-Code 헤더로 에러 코드를 보내면 i18n 매핑, 아니면 detail 텍스트 매칭 폴백.
      if (err?.code === 'email_already_registered'
          || detail.includes('already registered') || detail.includes('already exists')) {
        setErrors((p) => ({ ...p, email: t('auth.alert_email_already_used') }));
      } else if (err?.code === 'phone_already_registered') {
        const existingProvider = err?.response?.headers?.['x-existing-provider'];
        const msg = existingProvider
          ? t('errors.phone_already_registered_with_provider', { provider: existingProvider })
          : t('errors.phone_already_registered');
        setErrors((p) => ({ ...p, phone: msg }));
      } else {
        alert(translateApiError(err, t, 'auth.alert_signup_failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-[calc(100vh-59px)] bg-mono-50">
        <div className="w-full max-w-narrow mx-auto px-2.5 sm:px-5 pt-8 sm:pt-10 pb-12 sm:pb-16">
          {/* Logo */}
          <div className="flex justify-center"><img src="/images/logo/logo.png" alt="VIP VAN TOUR" className="h-10" /></div>

          {/* Card */}
          <Card variant="auth" className="mt-4">
            {/* Header with back arrow */}
            <div className="relative flex items-center justify-center mb-6">
              <button
                onClick={() => navigate(-1)}
                className="absolute left-0 p-1 text-mono-900 hover:text-orange-500 transition"
              >
                <Icon name="chevron-left" size={24} />
              </button>
              <h2 className="text-xl font-semibold text-mono-900">{t('common.signup')}</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <TextBox
                label={t('auth.email')}
                placeholder={t('auth.email_placeholder')}
                value={form.email}
                onChange={handleChange('email')}
                error={errors.email}
              />
              <div>
                <TextBox
                  label={t('auth.signup_password_label')}
                  placeholder={t('auth.password_placeholder')}
                  value={form.password}
                  onChange={handleChange('password')}
                  type="password"
                  error={errors.password}
                />
              </div>
              <TextBox
                label={t('auth.signup_password_confirm_label')}
                placeholder={t('auth.password_confirm_placeholder')}
                value={form.passwordConfirm}
                onChange={handleChange('passwordConfirm')}
                type="password"
                error={errors.passwordConfirm}
              />
              <TextBox
                label={t('auth.name')}
                placeholder={t('auth.name_placeholder')}
                value={form.name}
                onChange={handleChange('name')}
                error={errors.name}
              />

              {/* Terms */}
              <div className="pt-2">
                <label className="text-2sm font-bold text-mono-900 block mb-3">{t('auth.agreements')}</label>
                <div className="space-y-3">
                  <CheckBox
                    checked={agreements.all}
                    onChange={handleAllAgree}
                    label={t('common.all_agree')}
                  />
                  <div className="border-t border-mono-100 my-2" />
                  {TERMS_LIST.map((term) => (
                    <div key={term.key} className="flex items-center justify-between">
                      {/* BugHerd #131: [필수]/[선택] 텍스트 prefix 폐기 → CheckBox 의 required/optional
                          prop 으로 별표(*) / "(선택)" 인라인 노출. 전체 동의(상단) 는 표기 없음. */}
                      <CheckBox
                        checked={agreements[term.key]}
                        onChange={handleSingleAgree(term.key)}
                        label={term.label}
                        required={term.required}
                        optional={!term.required}
                      />
                      {term.slug ? (
                        <a href={`/policy/${term.slug}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="shrink-0 p-1 cursor-pointer text-mono-400 hover:text-mono-600 transition">
                          <Icon name="chevron-right" size={18} />
                        </a>
                      ) : (
                        <span className="w-7" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {errors.agreements && <p className="text-xs text-red-500 mt-1">{errors.agreements}</p>}

              <Button type="submit" level="level1" fullWidth className="mt-4" disabled={submitting}>
                {submitting ? t('common.loading') : t('common.signup')}
              </Button>
            </form>

      {termsModal && <TermsModal termKey={termsModal} onClose={() => setTermsModal(null)} />}

            {/* Login Link */}
            <p className="text-center text-sm text-mono-500 mt-6">
              {t('auth.have_account')}{' '}
              <Link to="/login" className="font-bold text-mono-900 hover:text-orange-500 transition">
                {t('common.login')}
              </Link>
            </p>
          </Card>
        </div>
      </main>
    </>
  );
}
